import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { buttonClass, inputClass } from '@/components/ui';

/**
 * The selling price and the online shop switch of ONE colour of a style.
 * Spec 014, FR-1420 and FR-1423.
 *
 * Guy, 29 October 2026 (Part 2.5): Stock on hand is the main screen, so the
 * price and "in the online shop" belong on the stock item panel too. They were
 * on three screens: the style page, Publishing, and (read only) the panel. Now
 * one component draws them on the style page and on the panel, and Publishing
 * is a list with links, so each control has one shape and one writer:
 *
 *   a colour with no shop row   POST  /api/styles/:id/publish  (catalogue.create)
 *   its price, its switch       PATCH /api/published/:id       (catalogue.edit)
 *
 * The price is per colour (spec 011 section 10), so the same row is changed
 * from either screen, and the other shows the new price on its next read.
 */
const toCents = (dollars: string) => Math.round(Number(dollars.trim().replace(/^\$/, '')) * 100);

async function send(path: string, method: string, body: unknown) {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(b?.error?.message ?? `Could not save (${res.status})`);
  }
}

export function ColourShopControls(
  { productId, colour, listing, may, onDone }:
  {
    productId: string;
    colour: string;
    listing: { id?: string; retail_price_cents: number; active: boolean } | null;
    /** catalogue.edit for a row that exists, catalogue.create for a new one. */
    may: { edit: boolean; create: boolean };
    onDone: () => void;
  },
) {
  const [price, setPrice] = useState(listing ? (listing.retail_price_cents / 100).toFixed(2) : '');
  const [error, setError] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: ({ path, method, body }: { path: string; method: string; body: unknown }) => send(path, method, body),
    onSuccess: () => { setError(null); onDone(); },
    onError: (e: Error) => setError(e.message),
  });

  const allowed = listing?.id ? may.edit : may.create;
  if (!allowed) return null;
  const typed = toCents(price);
  const priceOk = price.trim() !== '' && Number.isFinite(typed) && typed >= 0;
  const changed = listing != null && priceOk && typed !== listing.retail_price_cents;

  return (
    <span className="flex flex-wrap items-center gap-2">
      <input
        value={price}
        onChange={(e) => setPrice(e.target.value)}
        inputMode="decimal"
        placeholder="Price"
        aria-label={`Retail price of ${colour} in dollars`}
        className={`${inputClass} w-[92px] max-w-[92px] text-right font-[family-name:var(--font-mono)]`}
      />
      {listing == null || !listing.id ? (
        <button
          type="button"
          className={buttonClass('primary', 'sm')}
          disabled={act.isPending || !priceOk}
          onClick={() => act.mutate({
            path: `/api/styles/${productId}/publish`, method: 'POST',
            body: { colour, retail_price_cents: typed },
          })}
        >
          Put in the shop
        </button>
      ) : (
        <>
          {changed && (
            <button
              type="button"
              className={buttonClass('primary', 'sm')}
              disabled={act.isPending}
              onClick={() => act.mutate({
                path: `/api/published/${listing.id}`, method: 'PATCH', body: { retail_price_cents: typed },
              })}
            >
              Save
            </button>
          )}
          <button
            type="button"
            className={buttonClass('secondary', 'sm')}
            disabled={act.isPending}
            onClick={() => act.mutate({
              path: `/api/published/${listing.id}`, method: 'PATCH', body: { active: !listing.active },
            })}
          >
            {listing.active ? 'Take out of the shop' : 'Put in the shop'}
          </button>
        </>
      )}
      {error && <span role="alert" className="w-full text-[12px] text-[color:var(--color-danger-ink)]">{error}</span>}
    </span>
  );
}
